"use client";

import { usePathname } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import { gtmEventsService } from "./gtm-events-service";
import { GtmTrackData, GtmBaseEventData, AmsViewCustomData } from "./gtm-events-types";
import { getCookie } from "@/utilities/cookie";
import { OptimizelyTestCookieInfo } from "@/lib/optimizely";
import { debugLog } from "@/utilities/debug";
import { useAnalytics } from "@/contexts/analytics-context";
export type AmsViewData = GtmTrackData<"ams_view"> & AmsViewCustomData;
const useAMSViewListener = () => {
  const pathname = usePathname();
  const {
    setOneTrustGroupsReady
  } = useAnalytics();

  // Ref to track the current pathname being processed
  const pathnameRef = useRef<string | null>(null);

  // KEY CHANGE: Track pathname + timestamp to detect reloads
  const pageLoadIdRef = useRef<string | null>(null);

  // Ref to track if ams_view has been fired for the current page load
  // Reset on every pathname change to ensure one event per page visit
  const hasFiredForCurrentPageRef = useRef<boolean>(false);

  // Timer ref for debouncing route changes
  const routeTimerRef = useRef<number | null>(null);

  // State to store OneTrust consent data
  const [oneTrustData, setOneTrustData] = useState<GtmBaseEventData | AmsViewCustomData | null>(null);

  // Refs for tracking OneTrust data loading
  const oneTrustLoadedRef = useRef(false);
  const oneTrustDataRef = useRef<{
    OnetrustActiveGroups?: string;
    OptanonActiveGroups?: string;
  }>({});

  /**
   * Handler to update OneTrust consent data
   */
  const handleOneTrustData = (data: GtmBaseEventData | AmsViewCustomData) => {
    setOneTrustData(data);
  };

  /**
   * Core function to fire the ams_view event
   * This fires exactly once per page load/pathname change
   */
  const fireAmsView = async (): Promise<void> => {
    try {
      // Prevent duplicate firing for the same page visit
      if (hasFiredForCurrentPageRef.current) {
        debugLog("ams_view already fired for current page, skipping");
        return;
      }

      // Get base event data from GTM service
      const baseData = await gtmEventsService.getBaseEventData();

      // Initialize payload with base data and current OneTrust data
      const payload: AmsViewData = {
        ...(baseData || {}),
        ...(oneTrustData || {}) // Use current oneTrustData from state
      };

      // Attempt to add Optimizely feature test data from cookie
      try {
        const tests: {
          [key: string]: OptimizelyTestCookieInfo;
        } = getCookie("optimizelyFeatureTests") as {
          [key: string]: OptimizelyTestCookieInfo;
        } || getCookie("applyOptimizelyFeatureTests") as {
          [key: string]: OptimizelyTestCookieInfo;
        } || {};
        if (tests) {
          const firstDecision = Object.values(tests)[0] ?? {};
          if (firstDecision) {
            payload["optimizely-flagKey"] = String(firstDecision["optimizely-flagKey"] ?? "");
            payload["optimizely-ruleKey"] = firstDecision["optimizely-ruleKey"] ? String(firstDecision["optimizely-ruleKey"]) : null;
            payload["optimizely-variationKey"] = firstDecision["optimizely-variationKey"] ? String(firstDecision["optimizely-variationKey"]) : null;
            payload.decisionEventDispatched = !!firstDecision.decisionEventDispatched;
          }
        }
      } catch (err_0) {
        console.error("Error reading Optimizely cookie:", err_0);
      }

      // Fire the ams_view event
      gtmEventsService.track?.("ams_view", payload);

      // Update previous URL for tracking
      try {
        gtmEventsService.updatePreviousUrl?.(window.location.href);
      } catch (err_1) {
        console.error("Error updating previous URL:", err_1);
      }

      // Mark as fired for this page visit
      hasFiredForCurrentPageRef.current = true;
    } catch (err) {
      console.error("Error firing ams_view event:", err);
    }
  };

  /**
   * Main effect: Handles pathname changes and fires ams_view
   * This effect runs on every pathname change, ensuring one event per page visit
   */
  useEffect(() => {
    const WAIT_MS = 300; // Debounce delay to prevent rapid-fire events
    const currentPageLoadId = `${pathname}-${Date.now()}`;

    // Only reset if this is a genuinely new page load
    if (pageLoadIdRef.current !== currentPageLoadId) {
      hasFiredForCurrentPageRef.current = false;
      pageLoadIdRef.current = currentPageLoadId;
    }

    // Update the pathname ref
    pathnameRef.current = pathname;

    // Clear any existing timer
    if (routeTimerRef.current) {
      clearTimeout(routeTimerRef.current);
      routeTimerRef.current = null;
    }
    setTimeout(() => {
      fireAmsView();
    }, 0);
  }, [pathname]);

  /**
   * Effect: Initialize window.dataLayer
   * Ensures dataLayer exists as an array for GTM
   */
  useEffect(() => {
    if (Array.isArray(window.dataLayer)) {
      debugLog("window.dataLayer initialized as array");
    } else {
      console.warn("window.dataLayer is not an array, initializing");
      if (!window.dataLayer) {
        window.dataLayer = [];
      }
    }
  }, []);

  /**
   * Effect: Listen for OneTrust consent data
   * OneTrust fires events to dataLayer when consent preferences load/update
   */
  useEffect(() => {
    /**
     * Process OneTrust/Optanon events from dataLayer
     */
    const handleDataLayerEvent = (event: any) => {
      if (event.event === "OneTrustGroupsUpdated") {
        setOneTrustGroupsReady(true);
      }

      // Only process once
      if (oneTrustLoadedRef.current) return;

      // Collect OneTrust group data
      if (event.event === "OneTrustLoaded" && event.OnetrustActiveGroups) {
        oneTrustDataRef.current.OnetrustActiveGroups = event.OnetrustActiveGroups;
      }

      // Collect Optanon group data (alternative OneTrust property)
      if (event.event === "OptanonLoaded" && event.OptanonActiveGroups) {
        oneTrustDataRef.current.OptanonActiveGroups = event.OptanonActiveGroups;
      }

      // Once both are loaded, set the state and mark as complete
      if (oneTrustDataRef.current.OnetrustActiveGroups && oneTrustDataRef.current.OptanonActiveGroups) {
        const consentData = {
          OnetrustActiveGroups: oneTrustDataRef.current.OnetrustActiveGroups,
          OptanonActiveGroups: oneTrustDataRef.current.OptanonActiveGroups
        };
        handleOneTrustData(consentData);
        oneTrustLoadedRef.current = true;
      }
    };

    // Process any existing dataLayer events on mount
    if (Array.isArray(window.dataLayer)) {
      window.dataLayer.forEach(event_0 => {
        if (event_0.event === "OneTrustLoaded" || event_0.event === "OptanonLoaded" || event_0.event === "OneTrustGroupsUpdated") {
          handleDataLayerEvent(event_0);
        }
      });
    }

    // Intercept future dataLayer.push calls to capture OneTrust events
    if (window.dataLayer) {
      const originalPush = window.dataLayer.push;
      window.dataLayer.push = (...args: any[]): number => {
        const result = originalPush.apply(window.dataLayer, args);

        // Process each pushed event
        args.forEach(event_1 => {
          if (event_1.event === "OneTrustLoaded" || event_1.event === "OptanonLoaded" || event_1.event === "OneTrustGroupsUpdated") {
            handleDataLayerEvent(event_1);
          }
        });
        return result;
      };

      // Cleanup: restore original push function
      return () => {
        if (window.dataLayer) {
          window.dataLayer.push = originalPush;
        }
      };
    }
  }, []);
  return null;
};
export default useAMSViewListener;